import type {
    BridgeHealthResult,
    ConnectionHealthResult,
    PagedResult,
    AcombaCustomer,
    AcombaInvoiceAr,
    AcombaCustomerPayment,
    CompanyInformation,
    FunctionalityAccess,
    AcombaInvoiceArChangeSet,
    TransphereLinkResult,
    AcombaTransphereUnappliedPayments,
} from '../types/acombaBridge';
import { getBridgeNonce } from './bridgeNonce';
import { getBridgeCompany } from './bridgeCompany';

export class SessionExpiredError extends Error {
    readonly isSessionExpired = true;
    constructor() {
        super('Session expired');
        this.name = 'SessionExpiredError';
    }
}

export class InsufficientRightsError extends Error {
    readonly isInsufficientRights = true;
    constructor() {
        super('Insufficient Acomba rights');
        this.name = 'InsufficientRightsError';
    }
}

/**
 * The bridge answered, but not to us: this launch's nonce is missing or belongs to an earlier one.
 * Distinct from {@link SessionExpiredError} because reconnecting to Acomba cannot fix it — only
 * relaunching ComptaAI from Acomba can.
 */
export class BridgeNotOurLaunchError extends Error {
    readonly isNotOurLaunch = true;
    constructor() {
        super('The bridge only answers the Acomba launch that started it');
        this.name = 'BridgeNotOurLaunchError';
    }
}

const NONCE_REJECTED = 'caller_nonce_rejected';

// A ceiling on a hung connection, not a latency budget: the bridge is on loopback, but a candidate port
// can be held by something that completes the TLS handshake and then never answers, and a fetch with no
// signal waits on that for as long as the tab is open with nothing surfacing to the UI. Generous enough
// that no legitimate single-page read comes near it. Callers that want tighter still pass their own
// signal — AbortSignal.any settles on whichever fires first, so checkHealth keeps its 5s.
const BRIDGE_TIMEOUT_MS = 60_000;

/**
 * Every bridge call goes through here, so neither the launch nonce nor the timeout can be forgotten on
 * a new endpoint.
 */
async function bridgeFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
    const timeout = AbortSignal.timeout(BRIDGE_TIMEOUT_MS);
    const signal = init?.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
    const nonce = getBridgeNonce();
    const company = getBridgeCompany();
    if (!nonce && !company) return fetch(input, { ...init, signal });

    const headers = new Headers(init?.headers);
    if (nonce) headers.set('X-Bridge-Nonce', nonce);
    if (company) headers.set('X-Bridge-Company', company);
    return fetch(input, { ...init, headers, signal });
}

async function unauthorized(res: Response): Promise<Error> {
    // The bridge names the reason in the error envelope; anything else is Acomba refusing the session.
    try {
        const body: unknown = await res.json();
        if (typeof body === 'object' && body !== null && (body as { error?: unknown }).error === NONCE_REJECTED) {
            return new BridgeNotOurLaunchError();
        }
    } catch {
        // Not the JSON envelope; fall through.
    }
    return new SessionExpiredError();
}

async function assertServed(res: Response): Promise<void> {
    if (res.status === 401) throw await unauthorized(res);
    if (res.status === 403) throw new InsufficientRightsError();
}

async function fetchJson<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
    const res = await bridgeFetch(input, init);
    await assertServed(res);
    if (!res.ok) {
        throw new Error(`Bridge request failed: ${res.status}`);
    }
    return res.json() as Promise<T>;
}

function buildPagedUrl(base: string, path: string, cardPos?: number, count = 200): string {
    const url = new URL(path, base);
    if (cardPos !== undefined) url.searchParams.set('cardPos', String(cardPos));
    url.searchParams.set('count', String(count));
    return url.toString();
}

function buildModifiedSinceUrl(base: string, path: string, since: string, count: number, before?: string): string {
    const url = new URL(path, base);
    url.searchParams.set('since', since);
    url.searchParams.set('count', String(count));
    // Inclusive upper-bound paging cursor: resume just below a previous page's oldest record.
    if (before !== undefined) url.searchParams.set('before', before);
    return url.toString();
}

export const acombaBridgeService = {
    async checkHealth(baseUrl: string, signal?: AbortSignal): Promise<BridgeHealthResult> {
        const timeout = AbortSignal.timeout(5000);
        const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;
        return fetchJson<BridgeHealthResult>(`${baseUrl}/health`, { signal: combined });
    },

    async checkSession(baseUrl: string): Promise<ConnectionHealthResult> {
        return fetchJson<ConnectionHealthResult>(`${baseUrl}/api/connections/health`);
    },

    async getCustomers(baseUrl: string, cardPos?: number, count = 200): Promise<PagedResult<AcombaCustomer>> {
        return fetchJson<PagedResult<AcombaCustomer>>(buildPagedUrl(baseUrl, '/api/customers', cardPos, count));
    },

    // since (optional): invoice-date lower bound (the sync start date, ISO date string, e.g.
    // "2026-01-17"). The bridge walks all card positions but drops posted invoices dated before it (with
    // balances still computed for the kept set); in-transit invoices are returned regardless of date.
    async getInvoiceArs(baseUrl: string, cardPos?: number, count = 200, since?: string): Promise<PagedResult<AcombaInvoiceAr>> {
        const url = new URL(buildPagedUrl(baseUrl, '/api/invoice-ars', cardPos, count));
        if (since !== undefined) url.searchParams.set('since', since);
        return fetchJson<PagedResult<AcombaInvoiceAr>>(url.toString());
    },

    async getCustomerPayments(baseUrl: string, cardPos?: number, count = 200): Promise<PagedResult<AcombaCustomerPayment>> {
        return fetchJson<PagedResult<AcombaCustomerPayment>>(buildPagedUrl(baseUrl, '/api/customer-payments', cardPos, count));
    },

    // Incremental reads — bridge modified-since endpoints. `since` must be an ISO-8601 DateTime
    // string (e.g. "2026-07-01T00:00:00") matching the bridge's wire format. count caps the response.

    async getCustomersModifiedSince(baseUrl: string, since: string, count = 2000, before?: string): Promise<PagedResult<AcombaCustomer>> {
        return fetchJson<PagedResult<AcombaCustomer>>(
            buildModifiedSinceUrl(baseUrl, '/api/customers/modified-since', since, count, before),
        );
    },

    async getInvoiceArsModifiedSince(baseUrl: string, since: string, count = 5000, before?: string): Promise<PagedResult<AcombaInvoiceAr>> {
        return fetchJson<PagedResult<AcombaInvoiceAr>>(
            buildModifiedSinceUrl(baseUrl, '/api/invoice-ars/modified-since', since, count, before),
        );
    },

    async getCustomerPaymentsModifiedSince(baseUrl: string, since: string, count = 5000): Promise<PagedResult<AcombaCustomerPayment>> {
        return fetchJson<PagedResult<AcombaCustomerPayment>>(
            buildModifiedSinceUrl(baseUrl, '/api/customer-payments/modified-since', since, count),
        );
    },

    // Records actually modified since `since` (InTimeModified).
    // Unlike getInvoiceArsModifiedSince — a date-index read that can only answer "dated since" — this
    // sees a transaction entered today under an earlier invoice date, and an edit to an old invoice.
    //
    // Returns null when the connected bridge does not serve the endpoint (404). The bridge ships bundled
    // with Acomba per client site, so a site on an older build must keep syncing on the previous
    // date-index behaviour rather than be refused: that is what lets this land without raising
    // MIN_BRIDGE_VERSION, which would block sync outright for those sites (same reasoning as BR-15).
    //
    // startDate (optional): sync-window bound on the returned records.
    async getInvoiceArsChangedSince(
        baseUrl: string,
        since: string,
        startDate?: string,
    ): Promise<AcombaInvoiceArChangeSet | null> {
        const url = new URL('/api/invoice-ars/changed-since', baseUrl);
        url.searchParams.set('since', since);
        if (startDate !== undefined) url.searchParams.set('startDate', startDate);

        const res = await bridgeFetch(url.toString());
        await assertServed(res);
        if (res.status === 404) return null; // bridge predates the endpoint — caller falls back
        if (!res.ok) throw new Error(`Bridge request failed: ${res.status}`);
        return res.json() as Promise<AcombaInvoiceArChangeSet>;
    },

    async getCustomerByNumber(baseUrl: string, number: string): Promise<AcombaCustomer | null> {
        const res = await bridgeFetch(`${baseUrl}/api/customers/number/${encodeURIComponent(number)}`);
        await assertServed(res);
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`Bridge request failed: ${res.status}`);
        return res.json() as Promise<AcombaCustomer>;
    },

    // GET /api/customers/{cardPos}/invoice-ars — returns all invoices for a customer by card position.
    // Uses AcombaInvoiceArService.GetByCustomerAsync (customer-card-position index on the bridge).
    async getInvoiceArsByCustomerCardPos(baseUrl: string, customerCardPos: number, count = 2000, afterInvoiceNumber?: string, afterUnique?: number): Promise<PagedResult<AcombaInvoiceAr>> {
        const url = new URL(`/api/customers/${customerCardPos}/invoice-ars`, baseUrl);
        url.searchParams.set('count', String(count));
        // Exclusive paging cursor on the by-customer index's own order — (invoice number, then unique id),
        // NOT card position: card position is a physical slot, non-monotonic in that order, so a
        // card-position cursor would silently drop invoices whose slot sits below the page boundary.
        if (afterInvoiceNumber !== undefined) url.searchParams.set('afterInvoiceNumber', afterInvoiceNumber);
        if (afterUnique !== undefined) url.searchParams.set('afterUnique', String(afterUnique));
        return fetchJson<PagedResult<AcombaInvoiceAr>>(url.toString());
    },

    async getCompany(baseUrl: string): Promise<CompanyInformation> {
        return fetchJson<CompanyInformation>(`${baseUrl}/api/companies`);
    },

    async getAccess(baseUrl: string): Promise<FunctionalityAccess> {
        return fetchJson<FunctionalityAccess>(`${baseUrl}/api/access`);
    },

    /**
     * Asks the bridge to resolve an ACCEO Transphere payment link per invoice.
     *
     * The bridge resolves these locally with a credential that never leaves the customer's machine, so the
     * browser only ever sees finished URLs — never the credential and never a bearer token. Pass only
     * non-null `transphereId`s; invoices without one were never transmitted and have no link.
     */
    async resolveTransphereLinks(baseUrl: string, transphereIds: string[]): Promise<TransphereLinkResult> {
        return fetchJson<TransphereLinkResult>(`${baseUrl}/api/transphere/links`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ transphereIds }),
        });
    },

    /**
     * Asks the bridge what ACCEO Transphere collected over the window that Acomba has not recorded yet.
     *
     * Both halves are computed on the customer's machine — Transphere is called with the credential that
     * never leaves it, and the "already recorded" side is read from the company file — so the browser only
     * ever sees an aggregate.
     *
     * Check `unresolvedReason` before rendering anything: non-null means Transphere could not be asked, and
     * `count: 0` then means nothing at all.
     */
    async getTransphereUnappliedPayments(
        baseUrl: string,
        fromDate: string,
        toDate: string,
    ): Promise<AcombaTransphereUnappliedPayments> {
        const url = new URL(`${baseUrl}/api/transphere/unapplied-payments`);
        url.searchParams.set('fromDate', fromDate);
        url.searchParams.set('toDate', toDate);
        return fetchJson<AcombaTransphereUnappliedPayments>(url.toString());
    },
};
